import { BlastDoor } from "../../things/blastDoor";
import { DarkConcreteFloor } from "../../things/floors/darkConcreteFloor";
import { GraySlab } from "../../things/floors/graySlab";
import { JetpackLootBox } from "../../things/jetpackLootBox";
import { MetalLootBox } from "../../things/metalLootBox";
import { NightVisionLootBox } from "../../things/nightVisionLootBox";
import { OpenBlastDoorsTerminal } from "../../things/openBlastDoorsTerminal";
import { PowerPlantGateTerminal } from "../../things/powerPlantGateTerminal";
import { PowerPlantKeyCardLootBox } from "../../things/powerPlantKeyCardLootBox";
import { Sign } from "../../things/sign";
import { SlidingDoor } from "../../things/slidingDoor";
import { Tripwire } from "../../things/traps/tripwire";
import { VendingMachine, VendingMachineState } from "../../things/vendingMachine";
import { Direction } from "../../direction";
import type { PrefabData } from "./prefabData";
import { DrillLauncher } from "../../weapons/drillLauncher.ts";
import { PlasmaGun } from "../../weapons/plasmaGun.ts";
import { Cannon } from "../../weapons/cannon.ts";
import { CannonBurst } from "../../weapons/cannonBurst.ts";
import { LootBox } from "../../things/lootBox.ts";
import { MedicalLootBox } from "../../things/medicalLootBox.ts";
import { Medikit } from "../../items/medikit.ts";
import { KeyLootBox } from "../../things/keyLootBox.ts";
import { Key } from "../../items/key.ts";
import { GoldDrop } from "../../things/goldDrop.ts";
import { MissileGun } from "../../weapons/missileGun.ts";
import { ToxicSlug } from "../../mobs/toxicSlug.ts";

/**
 * Handcrafted wasteland-level-4 prefab.
 *
 * Placeholder level-changer destinations:
 *   U → 2, H → 3, J → 4 (playground numbers).
 * Consumers should pass overrides to buildPrefabWithOverrides to retarget them.
 */
export const wastelandLevel4PrefabData: PrefabData = {
	legend: {
		X: { class: BlastDoor.key, props: { group: "main" } },
		".": "ConcreteFloor",
		"_": GraySlab.key,
		",": DarkConcreteFloor.key,
		U: { class: "Upstairs", props: { destination: 2 } },
		E: "EmptyBarrel",
		"#": "MetalBlock",
		"+": "MetalDoor",
		"/": "WoodenDoor",
		"|": BlastDoor.key,
		"[": "ConcreteBlock",
		Q: "Rock",
		B: "BigContainer",
		"1": PowerPlantGateTerminal.key,
		l: "OfficeChair",
		c: "WoodenCabinet",
		d: "WoodenDesk",
		H: { class: "LadderDown", props: { destination: 3 } },
		J: { class: "LadderDown", props: { destination: 4 } },
		W: { class: SlidingDoor.key, props: { direction: Direction.N } },
		V: { class: SlidingDoor.key, props: { direction: Direction.N, group: "powerPlantGate" } },
		A: { class: SlidingDoor.key, props: { direction: Direction.S, group: "powerPlantGate" } },
		G: { class: SlidingDoor.key, props: { direction: Direction.N, group: "loot" } },
		"2": { class: OpenBlastDoorsTerminal.key, props: { group: "loot" } },
		S: ToxicSlug.key,
		T: Tripwire.key,
		j: JetpackLootBox.key,
		K: PowerPlantKeyCardLootBox.key,
		M: MetalLootBox.key,
		N: NightVisionLootBox.key,
		P: { class: Sign.key, props: { textKey: "sign_bullet_storage" } },
		v: { class: VendingMachine.key, props: { state: VendingMachineState.OFF } },
		"5": { class: MedicalLootBox.key, props: { lootKey: Medikit.key, count: 2 } },
		"6": { class: MetalLootBox.key, props: { lootKey: DrillLauncher.key, count: 5 } },
		"7": { class: LootBox.key, props: { lootKey: Cannon.key, count: 10 } },
		"8": { class: MetalLootBox.key, props: { lootKey: MissileGun.key, count: 5 } },
		"9": { class: MetalLootBox.key, props: { lootKey: PlasmaGun.key, count: 5 } },
		k: { class: KeyLootBox.key, props: { lootKey: Key.key, count:1 } },
		g: { class: GoldDrop.key, props: { amount: 50 } },
	},
	layers: [
		[
			"                                                  ",
			"                       ...................        ",
			"                   ____...................        ",
			"                   ____...................        ",
			"                   ____...................        ",
			"                       ...................        ",
			"                        ..................        ",
			"                        ..................        ",
			"                          ........                ",
			"                              ....                ",
			"                              ....                ",
			"                              ....                ",
			"                              ....                ",
			"                                                  ",
			"                                                  ",
			"                                  ...             ",
			"                                  ...             ",
			"                                  ...             ",
			"                                  ...             ",
			"                                  ...             ",
			"                                                  ",
			"                                                  ",
			"                    ,                             ",
			"                    ,,,,,,,,,,,,,,,,              ",
			"                   ,,,,,,,,,,,,,,,,,              ",
			"                   ,,,,,,,,,,,,,,,,,              ",
			"                                                  ",
			"                                                  ",
		],
		[
			"QQQQQQQQQQQQQQQQQQQQQQ#[[[[[[[[[[[[[[[[[[[#QQQQQQQ",
			"QQQQQQQQQQQQQQQQQQ#####2 d 5d[    [[      #QQQQQQQ",
			"    QQQQQQQQQQQQQQ#6gk#  l   [ U  [[ B  B #QQQQQQQ",
			"             QQQQQ#N  G     d[    [[      #QQQQQQQ",
			"              QQQQ#6g #   v d[[  [[[ B  B #       ",
			"              QQQQ#####dl    [    P[      #       ",
			"               QQQQQQQ[[           /      #       ",
			"                EEEE   [[          [  k  ##       ",
			"                        [[[/[[[/[#########        ",
			"                             [    ###EEEE         ",
			"                             [  1 ###             ",
			"                             [  l ###             ",
			"                             [[/[[###             ",
			"                                 S###             ",
			"                                  ###             ",
			"                                  V.V             ",
			"                                  A.A             ",
			"                                  ###             ",
			"                                  ###             ",
			"                                  ###             ",
			"                      QQQQQQQQQQQQ###             ",
			"                 QQQ QQQQQQQQQQQQ####             ",
			"                QQQ# ################QQQQQ        ",
			"            QQQQQQ## ##   #777 5 8###QQQQQQ       ",
			"           QQQQQQQ#   WT       K  ###QQQQQQQQQQQQQ",
			"QQQQQQQQQQQQQQQQQ## H # J #  N   9###QQQQQQQQQQQQQ",
			"QQQQQQQQQQQQQQQQQQ###################QQQQQQQQQQQQQ",
			"QQQQQQQQQQQQQQQQQQQQQQQQQQQQQQQQQQ###QQQQQQQQQQQQQ",
		],
	],
};
